import { useRef, useState } from 'react'

import { compact as _compact } from 'lodash'

import { EMPTY_ARRAY } from '~shared/core/primitives'
import { ColumnType } from '~shared/rundown/column'

import { gridSaveActions, saveKeys } from '../../../grid/grid.save'
import { getColumnOptionsOptimistic } from '../../../grid/grid.save.hooks'
import {
  gridSelectionActions,
  gridStateActions,
} from '../../../grid/grid.store'
import { cellCommands, createSaveCellCommand } from '../../cell.commands'
import { useCellKey } from '../../cell.context'
import { CellClickOutsideController, useCellColumn } from '../../cell.hooks'
import { parseCellKey } from '../../cell.schema'
import { CellFrame } from '../../CellFrame'
import { CellToolbar } from '../../toolbar/CellToolbar'

import { SelectColumnCellContentWrapper } from './SelectColumnCell.shared'

export function SelectColumnCellEditor() {
  const cellKey = useCellKey()
  const column = useCellColumn()

  // Assert that this `Column` is of the `type` that has options
  if (column.type !== ColumnType.SELECT) {
    throw new Error('Invalid column data')
  }

  const projectionOptions = column.options || EMPTY_ARRAY
  const [selectOptions] = useState(() =>
    getColumnOptionsOptimistic(column.id, projectionOptions),
  )

  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // TODO: Move into actions
  const handleSaveOptions = () => {
    const textAreaElm = textareaRef.current
    if (!textAreaElm) return

    const newSelectOptions = textAreaElm.value.split('\n')
    if (newSelectOptions.length < 0) return

    const newOptionsCleaned = _compact(newSelectOptions)

    // Release editor immediately
    gridStateActions.resetEditingCell()

    gridSaveActions.commit(saveKeys.column(column.id), {
      kind: 'columnOptions',
      columnId: column.id,
      content: {
        options: newOptionsCleaned,
      },
    })

    // Fire after potential re-renders
    requestAnimationFrame(() => {
      const { cueId } = parseCellKey(cellKey)
      gridSelectionActions.selectCell(cueId, column.id, { focus: true })
    })
  }

  const localCommandsEditing = [
    cellCommands.discard,
    // oxlint-disable-next-line react/refs -- False positive: ref is only read on when executed
    createSaveCellCommand({
      execute: () => handleSaveOptions(),
    }),
  ]

  return (
    <CellFrame>
      <SelectColumnCellContentWrapper>
        <textarea
          ref={textareaRef}
          autoFocus
          placeholder={'Option 1…\nOption 2…\nOption 3…'}
          defaultValue={selectOptions.join('\n')}
          rows={3}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.metaKey) {
              handleSaveOptions()
              return
            }
            if (e.key === 'Escape') {
              gridStateActions.editor.discard()
              return
            }
          }}
          className="p-1.5 bg-transparent border border-gray-700 rounded-sm text-sm w-full whitespace-pre-line"
        />
        <div className="mb-8 text-xs opacity-50">One option per line.</div>
      </SelectColumnCellContentWrapper>

      <CellClickOutsideController
        handler={() => {
          handleSaveOptions()
        }}
      />

      <CellToolbar
        onKeyTab={() => {
          textareaRef.current?.focus({ preventScroll: true })
        }}
        localCommands={localCommandsEditing}
      />
    </CellFrame>
  )
}
